import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, MapPin, Download, ChevronDown, ExternalLink } from 'lucide-react';
import { BRAND, CONTACT, FOOTER } from '@/content';
import { track } from '@/lib/track';

export default function Footer({ onEnquire }) {
  const [legalOpen, setLegalOpen] = useState(false);

  return (
    <footer data-testid="site-footer" className="border-t border-[#E4E6EB] bg-[#1C1D21] text-white" aria-label="Footer">
      {/* CTA band */}
      <div className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-4 py-14 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.3em] text-[#C92F4B]">Chapter 07 — Your Visit</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
              SEE IT F<span className="text-[#C92F4B]">O</span>R Y<span className="text-[#C92F4B]">O</span>URSELF.
            </h2>
            <p className="mt-2 max-w-md text-sm text-white/60">Book a site visit in under a minute. Walk the actual building before you decide.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button data-testid="footer-book-visit-btn" onClick={() => { track('book_visit', { placement: 'footer' }); onEnquire(); }} className="btn-primary">
              Book a Site Visit
            </button>
            <button
              data-testid="footer-brochure-btn"
              onClick={() => { track('brochure_download_intent', { placement: 'footer' }); onEnquire({ brochure: true }); }}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-[#C92F4B] hover:text-[#C92F4B]"
            >
              <Download size={16} /> Download Brochure
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="grid gap-10 md:grid-cols-[1.3fr_1fr_1fr]">
          <div>
            <img src={BRAND.logo} alt="Codename Metro One — Navade logo" className="h-12 w-auto rounded-lg bg-white p-1.5" />
            <p className="mt-5 text-sm font-bold uppercase tracking-[0.16em] text-white/90">{BRAND.codename} — {BRAND.location}</p>
            <p className="mt-1 text-xs text-white/50">Underlying registered project: <strong className="text-white/80">{BRAND.project}</strong></p>
            <p className="mt-1 text-xs text-white/50">Project by: <strong className="text-white/80">{BRAND.developer}</strong></p>
            <p className="mt-4 text-sm text-white/80">
              MahaRERA Registration No. <strong className="text-white">{BRAND.rera}</strong>
            </p>
            <a
              data-testid="footer-rera-link"
              href={BRAND.reraUrl}
              target="_blank" rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-[#C92F4B] underline-offset-4 hover:underline"
            >
              maharera.maharashtra.gov.in <ExternalLink size={11} />
            </a>
          </div>

          <div>
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.26em] text-white/50">Contact</p>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a data-testid="footer-phone-link" href={`tel:${CONTACT.phone}`} onClick={() => track('call_click', { placement: 'footer' })} className="flex items-center gap-2.5 text-white/85 transition-colors hover:text-[#C92F4B]">
                  <Phone size={15} /> {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <a data-testid="footer-email-link" href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 text-white/85 transition-colors hover:text-[#C92F4B]">
                  <Mail size={15} /> {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-white/60">
                <MapPin size={15} className="mt-0.5 shrink-0" />
                <span>Site Office: {CONTACT.siteOffice}</span>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-display text-[11px] font-bold uppercase tracking-[0.26em] text-white/50">Quick Links</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {[['Explore the Building', '#explore'], ['Homes', '#homes'], ['Floor Plans', '#plans'], ['Connectivity', '#connectivity'], ['Specifications', '#specifications']].map(([label, href]) => (
                <li key={href}>
                  <a data-testid={`footer-link-${label.split(' ')[0].toLowerCase()}`} href={href} className="text-white/70 transition-colors hover:text-[#C92F4B]">{label}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* disclaimer accordion */}
        <div className="mt-10 rounded-2xl border border-white/10">
          <button
            data-testid="footer-disclaimer-toggle"
            aria-expanded={legalOpen}
            onClick={() => setLegalOpen((v) => !v)}
            className="flex min-h-[52px] w-full items-center justify-between px-5 py-4 text-left"
          >
            <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/70">Disclaimer & Privacy</span>
            <ChevronDown size={16} className={`text-white/50 transition-transform duration-300 ${legalOpen ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence initial={false}>
            {legalOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }} className="overflow-hidden"
              >
                <p data-testid="footer-disclaimer-text" className="px-5 pb-5 text-[11px] leading-relaxed text-white/45">
                  {FOOTER.disclaimer} Enquiry details you share (name, mobile number, preferences) are used solely to respond to your request and are never sold. To have your details removed, write to {CONTACT.email}.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-[11px] text-white/40 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} {BRAND.developer}. Codename Metro One — Navade. All rights reserved.</p>
          <p>MahaRERA Registration No. {BRAND.rera}</p>
        </div>
      </div>
    </footer>
  );
}
